import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import type { FormTreeQuestion } from './formSchemaTypes';

export const DEFAULT_ROGER_LAYOUT_GENERATION_PROMPT =
  'Create a clean, clinician-friendly Roger Layout Schema for this form section. Include every question in the section as an answerable field, using compact grouping and tables where they make the form easier to scan.';

export type CustomGenerateTarget = {
  sectionName: string;
  displayName: string;
  questions: FormTreeQuestion[];
};

export function CustomGenerateDialog({
  target,
  defaultPrompt = DEFAULT_ROGER_LAYOUT_GENERATION_PROMPT,
  inProgress,
  onSubmit,
  onClose,
}: {
  target: CustomGenerateTarget | null;
  defaultPrompt?: string;
  inProgress: boolean;
  onSubmit: (prompt: string) => void;
  onClose: () => void;
}) {
  const [prompt, setPrompt] = useState(defaultPrompt);

  useEffect(() => {
    if (target != null) {
      setPrompt(defaultPrompt);
    }
  }, [target, defaultPrompt]);

  return (
    <Dialog
      open={target != null}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DialogContent className='max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Generate with custom prompt</DialogTitle>
          <DialogDescription>
            {target
              ? `Use a one-off prompt for ${target.displayName}.`
              : 'Use a one-off prompt for this section.'}
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-3'>
          <Textarea
            value={prompt}
            onChange={event => setPrompt(event.target.value)}
            rows={8}
            className='min-h-40'
            placeholder='Describe the layout style, grouping, and priorities...'
          />
          <div className='flex justify-end gap-2'>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button
              type='button'
              disabled={inProgress || prompt.trim().length === 0}
              onClick={() => {
                const trimmed = prompt.trim();
                if (!trimmed) return;
                onSubmit(trimmed);
                onClose();
              }}
            >
              {inProgress && (
                <Loader2 className='h-4 w-4 animate-spin mr-2' aria-hidden />
              )}
              Generate layout
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
